import axios from 'axios';

const HYPERLIQUID_INFO_URL = 'https://api.hyperliquid.xyz/info';

export type SelectedWallet = {
  walletAddress: string;
  chainId?: number;
};

export type HyperliquidPosition = {
  coin?: string;
  entryPx?: string;
  leverage?: {
    type?: string;
    value?: number;
  };
  liquidationPx?: string | null;
  positionValue?: string;
  returnOnEquity?: string;
  szi?: string;
  unrealizedPnl?: string;
};

export type HyperliquidClearinghouseState = {
  assetPositions?: Array<{
    position?: HyperliquidPosition;
  }>;
};

export type DexPositionRow = HyperliquidPosition & {
  id: string;
  walletAddress: string;
};

export type HyperliquidFrontendOpenOrder = {
  coin?: string;
  isPositionTpsl?: boolean;
  isTrigger?: boolean;
  limitPx?: string;
  oid?: number;
  orderType?: string;
  origSz?: string;
  reduceOnly?: boolean;
  side?: string;
  sz?: string;
  timestamp?: number;
  triggerCondition?: string;
  triggerPx?: string;
};

export type DexCurrentOrderRow = HyperliquidFrontendOpenOrder & {
  id: string;
  walletAddress: string;
};

export const toNumber = (value?: string | number | null) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export async function fetchHyperliquidWalletPositions(walletAddress: string): Promise<DexPositionRow[]> {
  const response = await axios.post<HyperliquidClearinghouseState>(
    HYPERLIQUID_INFO_URL,
    {
      type: 'clearinghouseState',
      user: walletAddress,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  const assetPositions = response.data?.assetPositions ?? [];

  return assetPositions
    .map((item, index) => {
      const position = item.position;
      if (!position) return null;

      return {
        ...position,
        id: `${walletAddress}-${position.coin ?? 'unknown'}-${index}`,
        walletAddress,
      };
    })
    .filter((item): item is DexPositionRow => Boolean(item))
    .filter((item) => toNumber(item.szi) !== 0);
}

export async function fetchHyperliquidWalletOrders(walletAddress: string): Promise<DexCurrentOrderRow[]> {
  const response = await axios.post<HyperliquidFrontendOpenOrder[]>(
    HYPERLIQUID_INFO_URL,
    {
      type: 'frontendOpenOrders',
      user: walletAddress,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  const orders = response.data ?? [];

  return orders.map((order, index) => ({
    ...order,
    id: `${walletAddress}-${order.oid ?? 'unknown'}-${index}`,
    walletAddress,
  }));
}
